import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { UIMemo } from '../model/UiShapes';

// 内部状态接口，包含所有方法
export interface MemoState {
  // State
  memos: UIMemo[];
  currentMemoId: string;
  currentMemo: UIMemo | null;
  editTitle: string; // 独立的标题编辑状态
  status: 'idle' | 'loading' | 'failed';
  isSyncing: boolean;
  failedMemoUpdateRecords: any[];
  currentViewMemoStatus: number;
  currentViewMemo: UIMemo | undefined;
  searchKey: string;
  searchMemos: UIMemo[];
  error: string | null; // 统一的错误状态

  // Actions
  setMemos: (memos: UIMemo[]) => void;
  addMemo: (memo: UIMemo) => void;
  updateMemo: (memo: UIMemo) => void;
  setSelectedMemoId: (memoId: string) => void;
  setEditTitle: (title: string) => void;
  setStatus: (status: 'idle' | 'loading' | 'failed') => void;
  setIsSyncing: (syncing: boolean) => void;
  addFailedMemoUpdate: (record: any) => void;
  clearFailedMemoUpdates: () => void;
  clear: () => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  setCurrentViewMemoStatus: (status: number) => void;
  setCurrentViewMemo: (memo: UIMemo | undefined) => void;
  setSearchKey: (searchKey: string) => void;
  setSearchMemos: (searchMemos: UIMemo[]) => void;
}

// 独立的actions对象
export const memoStoreActions = {
  setMemos: (set: any) => (memos: UIMemo[]) => {
    // 确保memos始终是数组类型
    if (!Array.isArray(memos)) {
      set({ memos: [] });
    } else {
      set({ memos });
    }
  },
  addMemo: (set: any) => (memo: UIMemo) =>
    set((state: MemoState) => ({
      memos: [...state.memos.filter((m) => m.guid !== memo.guid), memo],
      currentMemo: state.currentMemo?.guid === memo.guid ? memo : state.currentMemo
    })),
  updateMemo: (set: any) => (memo: UIMemo) => {
    set((state: MemoState) => ({
      memos: state.memos.map((m) => (m.guid === memo.guid ? memo : m)),
      currentMemo: state.currentMemo?.guid === memo.guid ? memo : state.currentMemo
    }));
  },

  setSelectedMemoId: (set: any, get: any) => (memoId: string) => {
    set({ currentMemoId: memoId });
    // 获取当前状态
    const currentState = get();

    const memo = currentState.memos.find((m: UIMemo) => m.guid === memoId);
    // 优化：避免不必要的状态更新
    // 检查memo是否真的发生了变化
    const currentMemo = currentState.currentMemo;
    const memoChanged = (memo === null && currentMemo !== null) || (memo !== null && currentMemo === null) || (memo && currentMemo && memo.guid !== currentMemo.guid);

    // 只有当memo实际发生变化时才更新状态
    if (!memoChanged && memo && currentMemo) {
      // 即使guid相同，也要检查其他关键属性是否变化
      const contentMarkChanged = memo.contentMark !== currentMemo.contentMark;
      const titleChanged = memo.title !== currentMemo.title;
      const groupIdChanged = memo.groupId !== currentMemo.groupId;

      // 如果关键属性有变化，也需要更新
      if (!contentMarkChanged && !titleChanged && !groupIdChanged) {
        // 没有实际变化，避免不必要的状态更新
        return;
      }
    }

    // 更新currentMemo
    set({ currentMemo: memo });

    // 同时更新editTitle为当前memo的标题
    if (memo && memo.title) {
      set({ editTitle: memo.title });
    } else {
      set({ editTitle: '' });
    }
  },
  setEditTitle: (set: any) => (title: string) => set({ editTitle: title }),
  setStatus: (set: any) => (status: 'idle' | 'loading' | 'failed') => set({ status }),
  setIsSyncing: (set: any) => (syncing: boolean) => set({ isSyncing: syncing }),
  addFailedMemoUpdate: (set: any) => (record: any) =>
    set((state: MemoState) => ({
      failedMemoUpdateRecords: [...state.failedMemoUpdateRecords, record]
    })),
  clearFailedMemoUpdates: (set: any) => () => set({ failedMemoUpdateRecords: [] }),
  clear: (set: any) => () => {
    set({
      memos: [],
      currentMemoId: '',
      currentMemo: null,
      status: 'idle',
      isSyncing: false,
      failedMemoUpdateRecords: [],
      currentViewMemoStatus: 0,
      currentViewMemo: undefined,
      searchKey: '',
      searchMemos: [],
      error: null
    });
  },
  setError: (set: any) => (error: string | null) => set({ error }),
  clearError: (set: any) => () => set({ error: null }),
  setCurrentViewMemoStatus: (set: any) => (status: number) => set({ currentViewMemoStatus: status }),
  setCurrentViewMemo: (set: any) => (memo: UIMemo | undefined) => set({ currentViewMemo: memo }),
  setSearchKey: (set: any) => (searchKey: string) => set({ searchKey }),
  setSearchMemos: (set: any) => (searchMemos: UIMemo[]) => set({ searchMemos })
};

export const useMemoStore = create<MemoState>()(
  devtools(
    (set, get) => ({
      // Initial state
      memos: [],
      currentMemoId: '',
      currentMemo: null,
      editTitle: '', // 独立的标题编辑状态
      status: 'idle',
      isSyncing: false,
      failedMemoUpdateRecords: [],
      currentViewMemoStatus: 0,
      currentViewMemo: undefined,
      searchKey: '',
      searchMemos: [],
      error: null, // 错误状态初始化为null

      // Actions
      setMemos: memoStoreActions.setMemos(set),
      addMemo: memoStoreActions.addMemo(set),
      updateMemo: memoStoreActions.updateMemo(set),
      setSelectedMemoId: memoStoreActions.setSelectedMemoId(set, get),
      setEditTitle: memoStoreActions.setEditTitle(set),
      setStatus: memoStoreActions.setStatus(set),
      setIsSyncing: memoStoreActions.setIsSyncing(set),
      addFailedMemoUpdate: memoStoreActions.addFailedMemoUpdate(set),
      clearFailedMemoUpdates: memoStoreActions.clearFailedMemoUpdates(set),
      clear: memoStoreActions.clear(set),
      setError: memoStoreActions.setError(set),
      clearError: memoStoreActions.clearError(set),
      setCurrentViewMemoStatus: memoStoreActions.setCurrentViewMemoStatus(set),
      setCurrentViewMemo: memoStoreActions.setCurrentViewMemo(set),
      setSearchKey: memoStoreActions.setSearchKey(set),
      setSearchMemos: memoStoreActions.setSearchMemos(set)
    }),
    {
      name: 'memo-store'
    }
  )
);
